import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader';
import { PageNotificationToast } from '../../components/notifications/PageNotificationToast';
import { CustomerFeedbackForm } from '../../components/forms/CustomerFeedbackForm';
import { usePageNotifications } from '../../hooks/usePageNotifications';
import { pageFeedbackToToast } from '../../lib/pageFeedbackToast';
import {
  submitCustomerFeedback,
  updateCustomerFeedback,
} from '../../lib/customerFeedbackSubmissions';
import { rebuildCffMonthOnSharePoint } from '../../lib/applicationFormSettings';
import { useAuth } from '../../contexts/AuthContext';
import { usePortal } from '../../contexts/PortalContext';
import { useFormWorkflowPermissions } from '../../hooks/useFormWorkflowPermissions';
import { resolveInternalSubmitterLabel } from '../../lib/applicationFormSubmitter';
import { useUnifiedPortalUserLookup } from '../../hooks/useUnifiedPortalUserLookup';
import {
  EMPTY_CUSTOMER_FEEDBACK_FORM,
  type ApplicationSubmission,
  type CustomerFeedbackFormData,
} from '../../types/applicationForm';

export type CffNewNavigateState = {
  cffNotice?: string;
  cffError?: string;
};

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

async function queueCffSharePointSync(
  submissionId: string,
  form: CustomerFeedbackFormData,
  applicationNo: string,
): Promise<void> {
  try {
    await rebuildCffMonthOnSharePoint(applicationNo, { submissionId, form });
  } catch (err) {
    console.warn('CFF SharePoint sync queue failed:', err);
  }
}

export function CustomerFeedbackNewPage() {
  const { basePath, kind } = usePortal();
  const isDem = kind === 'dem';
  const navigate = useNavigate();
  const { profile, authUser } = useAuth();
  const { rows: unifiedUsers } = useUnifiedPortalUserLookup();
  const { canSubmitInternal, loading: permissionsLoading } =
    useFormWorkflowPermissions('customer-feedback');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<CustomerFeedbackFormData>(() => ({
    ...EMPTY_CUSTOMER_FEEDBACK_FORM,
    date: todayIso(),
    submitted_by: '',
  }));
  /** Survives a failed sync so retry updates the same row instead of creating another. */
  const createdIdRef = useRef<string | null>(null);
  const lastErrorToastRef = useRef<string | null>(null);
  const { push: pushToast, activeToast, dismissToast } = usePageNotifications();

  useEffect(() => {
    if (error && error !== lastErrorToastRef.current) {
      lastErrorToastRef.current = error;
      pushToast(pageFeedbackToToast({ kind: 'error', message: error }));
    }
    if (!error) lastErrorToastRef.current = null;
  }, [error, pushToast]);

  const listPath = basePath ? `${basePath}/customer-feedback` : '/customer-feedback';

  const submitterLabel = useMemo(
    () => resolveInternalSubmitterLabel(profile, authUser, unifiedUsers) ?? '',
    [profile, authUser, unifiedUsers],
  );

  const goToSubmission = (id: string, state?: CffNewNavigateState) => {
    navigate(`${listPath}/${id}`, { replace: true, state });
  };

  const handleSubmit = async () => {
    if (!form.company_name.trim() && !form.company_code.trim()) {
      setError('Enter company name or company code.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload: CustomerFeedbackFormData = {
        ...form,
        submitted_by: submitterLabel,
      };
      let row: ApplicationSubmission;
      if (createdIdRef.current) {
        row = await updateCustomerFeedback(createdIdRef.current, payload);
      } else {
        row = await submitCustomerFeedback(
          payload,
          submitterLabel,
          authUser?.email ?? profile?.email ?? undefined,
        );
        createdIdRef.current = row.id;
      }
      void queueCffSharePointSync(
        row.id,
        row.form_data as CustomerFeedbackFormData,
        row.application_no,
      );
      goToSubmission(row.id, {
        cffNotice: 'Submitted. You can keep editing, then Post with a signed copy when ready.',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit.');
    } finally {
      setBusy(false);
    }
  };

  const pageClass = `af-applications-page af-form-page${isDem ? ' cff-page--floating' : ''}`;

  if (permissionsLoading) {
    return (
      <div className={pageClass}>
        <p className="page-lead">Checking submission permissions…</p>
      </div>
    );
  }

  if (!canSubmitInternal) {
    return (
      <div className={pageClass}>
        {isDem ? (
          <>
            <Link to={listPath} className="btn-secondary delivery-page__back">
              Back to list
            </Link>
            <p className="alert alert-error">You do not have permission to create this form.</p>
          </>
        ) : (
          <PageHeader
            title="New Customer Feedback Form"
            description="You do not have permission to create this form."
            actions={
              <Link to={listPath} className="btn-secondary btn-sm">
                Back to list
              </Link>
            }
          />
        )}
      </div>
    );
  }

  return (
    <div className={pageClass}>
      {isDem ? (
        <Link to={listPath} className="btn-secondary delivery-page__back">
          Back to list
        </Link>
      ) : (
        <PageHeader
          title="New Customer Feedback Form (CFF)"
          description="Submit the form first. After submit you can edit, then Post with a signed copy."
          actions={
            <Link to={listPath} className="btn-secondary btn-sm">
              Back to list
            </Link>
          }
        />
      )}

      {activeToast
        ? createPortal(
            <PageNotificationToast notification={activeToast} onDismiss={dismissToast} />,
            document.body,
          )
        : null}

      <div className="card cff-form-card">
        <CustomerFeedbackForm
          value={form}
          onChange={setForm}
          submittedByDisplayName={submitterLabel}
        />
        <div className="cff-form__actions">
          <button
            type="button"
            className="btn-primary"
            disabled={busy}
            onClick={() => void handleSubmit()}
          >
            {busy ? 'Working…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}
